import * as THREE from "three";
import Experience from "../Experience";
import { Line2, LineGeometry, LineMaterial } from "three-fatline";
import { secondaryColor } from "../Utils/styles";
import type Sizes from "../Utils/Sizes";
import type Camera from "../Camera";

export default class CameraPathTracker {
  experience!: Experience;
  scene!: THREE.Scene;
  camera!: THREE.PerspectiveCamera;
  sizes!: Sizes;
  points: number[] = [];
  active = true;
  opacity = 0.4;
  lineWidth = 3;
  lastUpdateTime = 0;
  updateInterval = 100;
  line: Line2 | null = null;
  lineGeometry: LineGeometry | null = null;
  lineMaterial: LineMaterial | null = null;
  cameraWrapper!: Camera;
  onControlsRest: () => void;

  constructor(active = true, points: number[] = []) {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.cameraWrapper = this.experience.camera;
    this.camera = this.cameraWrapper.instance;
    this.sizes = this.experience.sizes;
    this.points = points;
    this.active = active;
    if (this.active) {
      this.opacity = 0.4;
      this.lineWidth = 3;
    } else {
      this.opacity = 0.25;
      this.lineWidth = 1;
    }
    this.lastUpdateTime = 0;
    this.updateInterval = 100;
    this.throttledUpdateLine = this.throttledUpdateLine.bind(this);
    this.onControlsRest = () => {
      this.throttledUpdateLine();
    };
    this.init();
  }

  async init() {
    const controls = this.cameraWrapper?.getCameraControls?.();
    controls?.addEventListener("rest", this.onControlsRest);
  }

  async throttledUpdateLine() {
    const currentTime = Date.now();
    if (currentTime - this.lastUpdateTime >= this.updateInterval) {
      await this.updateLine();
      this.lastUpdateTime = currentTime;
    }
  }

  async updateLine() {
    if (this.active) {
      const cameraPosition = new THREE.Vector3();
      this.camera.getWorldPosition(cameraPosition);
      const lastPointPos = cameraPosition.toArray();
      this.points.push(...lastPointPos);
    }

    if (this.points.length <= 6) {
      return null;
    }

    const MAX_POINTS = 1000;
    if (this.points.length / 3 > MAX_POINTS) {
      this.points.splice(0, 3);
    }

    const nextGeometry = new LineGeometry();
    nextGeometry.setPositions(this.points);

    if (!this.lineMaterial) {
      this.lineMaterial = new LineMaterial({
        color: new THREE.Color(secondaryColor).getHex(),
        linewidth: 2,
        opacity: this.opacity,
        transparent: true,
        resolution: new THREE.Vector2(this.sizes.width, this.sizes.height),
      });
    }

    if (this.line) {
      this.line.geometry.dispose();
      this.line.geometry = nextGeometry;
      this.line.computeLineDistances();
    } else {
      this.line = new Line2(nextGeometry, this.lineMaterial);
      this.line.name = "CameraLine";
      this.line.computeLineDistances();
      this.scene.add(this.line);
    }

    this.line.userData.points = [...this.points];
    this.lineGeometry = nextGeometry;
    return this.line;
  }

  destroy() {
    const controls = this.cameraWrapper?.getCameraControls?.();
    if (controls) {
      controls.removeEventListener("rest", this.onControlsRest);
    }

    if (this.line) {
      this.scene.remove(this.line);
      this.line.geometry.dispose();
      this.line = null;
    }
    if (this.lineMaterial) {
      this.lineMaterial.dispose();
      this.lineMaterial = null;
    }
    this.lineGeometry = null;

    this.points = [];
    this.active = false;
  }
}
